import React, { useEffect, useState } from "react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import useNumericInput from "../../../hooks/useNumericInput";
import { useLoader } from "../../../utils/LoaderUtils";
import ProductAPI from "../../../api/inventory/ProductApi";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";

function SingleInventory({ onSubmit, prevStep, productId }) {
  useNumericInput();

  if (!productId) {
    productId = sessionStorage.getItem("productId");
  }

  const [formData, setFormData] = useState({
    costPrice: "",
    mrp: "",
    price: "",
    discount: "",
    alertQuantity: "5",
    availableStock: "",
    productId,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (
      [
        "costPrice",
        "mrp",
        "price",
        "discount",
        "availableStock",
        "alertQuantity",
      ].includes(name)
    ) {
      if (value === "" || /^\d+(\.\d{0,2})?$/.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          _lastChanged: name, // track source
        }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const [expectedProfit, setExpectedProfit] = useState("");
  const [errors, setErrors] = useState({});
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    const { mrp, price, discount, costPrice, _lastChanged } = formData;

    const mrpVal = Number(mrp);
    const priceVal = Number(price);
    const discountVal = Number(discount);
    const costVal = Number(costPrice);

    // Profit
    if (!isNaN(priceVal) && !isNaN(costVal)) {
      setExpectedProfit(Math.round(priceVal - costVal));
    } else {
      setExpectedProfit("");
    }

    // Price → Discount
    if (
      _lastChanged === "price" &&
      mrpVal > 0 &&
      priceVal >= 0 &&
      priceVal <= mrpVal
    ) {
      const d = ((mrpVal - priceVal) / mrpVal) * 100;
      setFormData((p) => ({ ...p, discount: d.toFixed(2) }));
    }

    // Discount → Price
    if (
      _lastChanged === "discount" &&
      mrpVal > 0 &&
      discountVal >= 0 &&
      discountVal <= 100
    ) {
      const newPrice = mrpVal * (1 - discountVal / 100);
      setFormData((prev) => ({ ...prev, price: Math.round(newPrice) }));
    }
  }, [formData.mrp, formData.price, formData.discount, formData.costPrice]);

  useEffect(() => {
    fetchStep2SinglePrice();
  }, []);

  const fetchStep2SinglePrice = async () => {
    try {
      const res = await ProductAPI.fetchStep2SinglePrice(productId);

      if (res.code === 200) {
        const result = res.data;

        setFormData({
          costPrice: result?.costPrice?.toString() || "",
          mrp: result?.mrp?.toString() || "",
          price: result?.price?.toString() || "",
          discount: result?.discount?.toString() || "",
          availableStock: result?.stock?.toString() || "",
          alertQuantity: result?.alertQuantity?.toString() || "5",
          productId,
        });
      }
    } catch (err) {}
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.costPrice) {
      newErrors.costprice = "Cost Price is required";
    }
    if (!formData.price) {
      newErrors.price = "Price is required";
    }
    if (!formData.mrp) {
      newErrors.mrp = "MRP is required";
    }
    if (!formData.discount) {
      newErrors.discount = "Discount is required";
    }

    if (Number(formData.mrp) < Number(formData.price)) {
      newErrors.price = "Price cannot be greater than MRP";
    }

    if (Number(formData.discount) > 100) {
      newErrors.discount = "Discount cannot be more than 100%";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showLoader();

    if (!validate()) {
      hideLoader();
      return;
    }

    try {
      const res = await ProductAPI.updateStep2SinglePrice(productId, formData);

      if (res.code === 200) {
        showSuccess(res.message);
        onSubmit(formData);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div className="card-body">
        <form method="post" onSubmit={handleSubmit}>
          <h5 className="mb-2">Price Information</h5>

          <div className="row mb-3">
            <div className="col-lg-4 col-sm-4">
              <label className="form-label">
                Cost Price <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control only-numeric"
                placeholder="Cost Price"
                name="costPrice"
                value={formData.costPrice}
                onChange={handleChange}
              />
              {errors.costprice && (
                <span className="errors text-danger">{errors.costprice}</span>
              )}
            </div>
            <div className="col-lg-4 col-sm-4">
              <label className="form-label">
                MRP <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control only-numeric"
                placeholder="MRP"
                name="mrp"
                value={formData.mrp}
                onChange={handleChange}
              />
              {errors.mrp && (
                <span className="errors text-danger">{errors.mrp}</span>
              )}
            </div>
            <div className="col-lg-4 col-sm-4">
              <label className="form-label">
                Price <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control only-numeric"
                placeholder="Price"
                name="price"
                value={formData.price}
                onChange={handleChange}
              />
              <div>
                <span className="fs--1">
                  Expected Profit:{" "}
                  <span className="text-success">₹{expectedProfit || ""}</span>
                </span>
              </div>
              {errors.price && (
                <span className="errors text-danger">{errors.price}</span>
              )}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-lg-4 col-sm-4">
              <label className="form-label">
                Discount (%) <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Discount (%)"
                name="discount"
                value={formData.discount}
                onChange={handleChange}
              />
              {errors.discount && (
                <span className="errors text-danger">{errors.discount}</span>
              )}
            </div>
          </div>

          <hr></hr>
          <h5 className="mb-2">Inventory Information</h5>
          <div className="row mb-3">
            <div className="col-lg-4 col-sm-4">
              <label className="form-label">
                Stock <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control only-numeric"
                placeholder="Stock"
                name="availableStock"
                value={formData.availableStock}
                onChange={handleChange}
              />
              {errors.availablestock && (
                <span className="errors text-danger">
                  {errors.availablestock}
                </span>
              )}
            </div>
            <div className="col-lg-4 col-sm-4">
              <label className="form-label">
                Alert Quantity <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control only-numeric"
                placeholder="Alert Quantity"
                name="alertQuantity"
                value={formData.alertQuantity}
                onChange={handleChange}
              />
            </div>

            {Number(formData.availableStock) <=
              Number(formData.alertQuantity) && (
              <span className="text-danger errors">
                There is already low quantity available in stock of this product
              </span>
            )}
          </div>

          <div className="row">
            <div className="col-md-12">
              <button
                type="button"
                id="submitBtn"
                className="me-2 btn btn-primary shadow-none"
                onClick={prevStep}
              >
                Prev
              </button>
              <button
                type="submit"
                id="submitBtn"
                className="btn btn-primary shadow-none"
              >
                Next
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default SingleInventory;
